import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import {
  createProfileQueryKey,
  createExperienceCollaboratorsQueryKey,
} from '@/utils/queryKeys';
import type {
  TransferExperienceOwnershipMutation,
  TransferExperienceOwnershipMutationVariables,
} from './useTransferOwnership.generated';

const query = gql`
  mutation TransferExperienceOwnership(
    $input: TransferExperienceOwnershipInput!
  ) {
    transferExperienceOwnership(input: $input)
  }
`;

export const useTransferOwnership = () => {
  const { experience } = usePageExperienceContext();
  const experienceId = String(experience?.id);
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation<
    TransferExperienceOwnershipMutation,
    QueryError,
    TransferExperienceOwnershipMutationVariables
  >({
    mutationFn: (variables) => {
      return gqlClient.request<
        TransferExperienceOwnershipMutation,
        TransferExperienceOwnershipMutationVariables
      >(query, variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: createProfileQueryKey() });

      return queryClient.invalidateQueries({
        queryKey: createExperienceCollaboratorsQueryKey(experienceId),
      });
    },
  });

  const transferOwnership = (
    userId: string,
    options?: MutateOptions<
      TransferExperienceOwnershipMutation,
      QueryError,
      TransferExperienceOwnershipMutationVariables
    >
  ) => mutate({ input: { experienceId, userId } }, options);

  return {
    transferOwnership,
    isPending,
  };
};
